// src/components/SEO.js
// Reusable SEO component with meta tags and JSON-LD structured data

import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Rosalytics';
const SITE_URL = 'https://rosalytics.com';
const DEFAULT_TITLE = 'Rosalytics – AI, GenAI & Advanced Analytics for Modern Enterprises';
const DEFAULT_DESCRIPTION = 'Rosalytics partners with enterprises to design AI, GenAI, and analytics solutions that drive operational intelligence and digital transformation.';

/**
 * SEO Component
 * @param {Object} props
 * @param {string} [props.title] - Page title (will be appended with site name)
 * @param {string} [props.description] - Page description
 * @param {string} [props.canonical] - Canonical URL (defaults to current path)
 * @param {string} [props.image] - OG image URL
 * @param {string} [props.type] - OG type (website, article, etc.)
 * @param {boolean} [props.noindex] - Add noindex,nofollow robots directive
 * @param {Object} [props.article] - Article metadata (author, publishedTime, modifiedTime)
 * @param {Object} [props.schema] - JSON-LD structured data object
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image,
  type = 'website',
  noindex = false,
  article,
  schema
}) => {
  // Build full title
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : DEFAULT_TITLE;

  // Build canonical URL
  const canonicalUrl = canonical
    ? `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}`
    : undefined;

  // Build image URL
  const imageUrl = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : undefined;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {noindex && <meta name="googlebot" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* Article-specific meta (for blog posts) */}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}

      {/* JSON-LD Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-built schemas for common use cases

/**
 * Organization Schema for homepage
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Rosalytics",
  "description": DEFAULT_DESCRIPTION,
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo512.png`,
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "info@rosalytics.com",
    "contactType": "customer service"
  },
  "address": [
    {
      "@type": "PostalAddress",
      "addressLocality": "Austin",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "Pune",
      "addressCountry": "IN"
    }
  ]
};

/**
 * WebSite Schema for homepage
 */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Rosalytics",
  "url": SITE_URL
};

/**
 * Generate Article Schema
 * @param {Object} article - Article data
 * @returns {Object} Article schema
 */
export const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description || article.excerpt,
  "image": article.image,
  "author": {
    "@type": "Person",
    "name": article.author?.name || "Rosalytics Team"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Rosalytics",
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/logo512.png`
    }
  },
  "datePublished": article.publishedDate,
  "dateModified": article.modifiedDate || article.publishedDate,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_URL}${article.url}`
  }
});

/**
 * Combined schema for homepage (Organization + WebSite)
 */
export const homepageSchema = [organizationSchema, websiteSchema];

export default SEO;
